import { useLayoutEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import { toast } from "react-toastify"
import { NotFound } from "../../../components/ui-kit/NotFound"
import { EditProjectTabs } from "./Tabs"
import { useDeleteProjectMutation, useGetProjectQuery } from "../../../api"
import { routesProfile } from "../../../consts"

export const EditProject = () => {
    useLayoutEffect(() => {
        document.title = 'Редактирование проекта'
    }, [])

    const { id } = useParams<{ id: string }>() 
    const projectId = Number(id)
    const { data: project, isLoading, isError } = useGetProjectQuery(projectId)
    const navigate = useNavigate()

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [deleteProject, { isLoading: isDeletingProject }] = useDeleteProjectMutation()

    const myProjectsPath = useMemo(() => `/${routesProfile.PROJECTS.path}/mine`, [])

    if (isLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" height="100%">
                <CircularProgress />
            </Stack>
        )
    }

    if (isError || !project) {
        return <NotFound text="Проект не найден" />
    }

    const permissions = project.viewer?.permissions
    const canAccess = permissions?.can_edit_content || permissions?.can_moderate
    if (!canAccess) {
        return <NotFound text="У вас нет доступа к редактированию этого проекта" />
    }

    const handleConfirmDelete = async () => {
        try {
            await deleteProject({ projectId }).unwrap()
            setIsDeleteModalOpen(false)
            toast.success("Проект удалён")
            navigate(myProjectsPath, { replace: true })
        } catch (err) {
            const status = (err as any)?.status ?? (err as any)?.originalStatus
            if (status === 403) {
                toast.error("Нет прав удалить проект")
                return
            }
            if (status === 404) {
                toast.error("Проект не найден или уже удалён")
                return
            }
            toast.error("Не удалось удалить проект")
        }
    }

    return (
        <Box
            sx={(theme) => ({
                /** Same clearance as `PagesBox` margin: fixed bottom nav (48px) + safe area + buffer; padding extends scroll height when outer margin is ineffective. */
                [theme.breakpoints.down('md')]: {
                    paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
                },
            })}
        >
            <EditProjectTabs
                title="Редактирование проекта"
                projectStatus={project.status}
                permissions={permissions}
            />

            {permissions?.can_delete_project && (
            <>
            <Stack alignItems="center" px={1.5} width="100%" sx={{ mt: 2 }}>
                <Stack width="100%" maxWidth={{ xs: '736px', md: '840px' }}>
                    <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
                        <Stack gap={1.5}>
                            <Typography variant="black-16-semiBold">Опасная зона</Typography>
                            <Typography variant="black-14-normal" color="text.secondary">
                                Удаление проекта необратимо. Перед удалением убедитесь, что вам больше не нужны данные проекта.
                            </Typography>
                            <Stack direction="row">
                                <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)}>
                                    Удалить проект
                                </Button>
                            </Stack>
                        </Stack>
                    </Card>
                </Stack>
            </Stack>

            <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <DialogTitle>Удалить проект</DialogTitle>
                <DialogContent>
                    <Typography variant="black-14-normal">
                        Проект будет удалён и исчезнет из личного кабинета. Действие нельзя отменить.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="follow"
                        onClick={() => setIsDeleteModalOpen(false)}
                        disabled={isDeletingProject}
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmDelete}
                        disabled={isDeletingProject}
                    >
                        {isDeletingProject ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <CircularProgress size={16} color="inherit" />
                                <span>Удаление...</span>
                            </Stack>
                        ) : (
                            "Удалить"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            </>
            )}
        </Box>
    )
}
